import { useEffect, useState } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Users, Clock, Video, Play, Download,
  BarChart2, Eye, TrendingUp, Star, Globe,
  Calendar, Radio
} from 'lucide-react'
import { Button } from '@/clientWeb/components/ui/button'
import { Badge } from '@/clientWeb/components/ui/badge'
import { EmptyState } from '@/clientWeb/components/ui/empty-state'
import { FullPageDotsLoader } from '@/clientWeb/components/ui/page-loader'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts'
import {
  webinarPublicService,
  type WebinarPastDetail,
  type WebinarRegistrationEntry,
} from './services/webinar-public.service'

type TabId = 'overview' | 'recording' | 'registrations' | 'analytics'

function useTabs() {
  const t = useTranslation()
  return [
    { id: 'overview' as TabId, label: t('webinar.overview'), Icon: BarChart2 },
    { id: 'recording' as TabId, label: t('webinar.recording'), Icon: Video },
    { id: 'registrations' as TabId, label: t('webinar.registrations'), Icon: Users },
  ]
}

export default function PastWebinarDetails() {
  const t = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState<TabId>('overview')
  const TABS = useTabs()
  const [w, setW] = useState<WebinarPastDetail | null>(null)
  const [registrations, setRegistrations] = useState<WebinarRegistrationEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      webinarPublicService.getPastDetail(id ?? 'w1'),
      webinarPublicService.listRegistrations(id ?? 'w1'),
    ])
      .then(([detail, regs]) => {
        if (cancelled) return
        setW(detail)
        setRegistrations(regs)
      })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load webinar', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading || !w) return <FullPageDotsLoader />

  const attendanceRate = Math.round((w.attended / w.registrations) * 100)

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 border-b border-stroke-100 dark:border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Link to="/webinar" className="hover:text-foreground transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('webinar.webinars')}
          </Link>
          <span>/</span>
          <span className="text-foreground truncate">{w.title}</span>
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-lg font-bold text-foreground">{w.title}</h1>
              <Badge className="text-[10px] bg-muted text-muted-foreground border border-border">{t('webinar.completed')}</Badge>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="w-3 h-3" />{w.date}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" />{w.startTime} – {w.endTime}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Radio className="w-3 h-3 text-red-400" />{t('webinar.liveEnded')}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => toast.success({ title: t('webinar.report'), description: `${w.title} — ${t("common.downloadStarted") ?? "Downloading…"}` })}>
              <Download className="w-3.5 h-3.5" /> {t('webinar.report')}
            </Button>
            <Button size="sm" className="text-xs gap-1.5" onClick={() => navigate(`/webinar/${id}/analytics`)}>
              <TrendingUp className="w-3.5 h-3.5" /> {t('nav.analytics')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 sm:px-6 bg-card border-b border-border shrink-0 overflow-x-auto scrollbar-hide">
        {TABS.map(({ id: tid, label, Icon }) => (
          <button
            key={tid}
            onClick={() => setTab(tid)}
            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors shrink-0
              ${tab === tid ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">

          {/* Overview */}
          {tab === 'overview' && (
            <motion.div key="ov" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 sm:p-6 space-y-5">
              {/* Stats - styled like PaymentHistory summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: t('webinar.registrations'), value: w.registrations, icon: Globe, color: 'text-blue-500', bg: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20' },
                  { label: t('webinar.attended'), value: `${w.attended} (${attendanceRate}%)`, icon: Users, color: 'text-emerald-500', bg: 'bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20' },
                  { label: t('webinar.peakConcurrent'), value: w.peakConcurrent, icon: Radio, color: 'text-violet-500', bg: 'bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20' },
                  { label: t('webinar.avgWatchTime'), value: w.avgWatchTime, icon: Clock, color: 'text-amber-500', bg: 'bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20' },
                ].map(({ label, value, icon: Icon, color, bg }, i) => (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-4 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden"
                  >
                    {/* Decorative Background Wave */}
                    <div className={`absolute inset-0 ${bg}`}>
                      <svg
                        className="absolute bottom-0 right-0 w-full h-full"
                        viewBox="0 0 200 120"
                        fill="none"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z"
                          fill="currentColor"
                          className="text-white/30 dark:text-white/10"
                        />
                      </svg>
                    </div>

                    {/* Content aligned with PaymentHistory cards */}
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-card/50 dark:bg-card/10 flex items-center justify-center shrink-0">
                        <Icon className={`w-4 h-4 ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xl font-bold text-foreground leading-none truncate">{value}</p>
                        <p className="text-sm text-muted-foreground mt-1 truncate">{label}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Satisfaction */}
              <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-5 h-5 ${s <= Math.round(w.satisfaction) ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`} />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{w.satisfaction}/5 {t('webinar.satisfaction')}</p>
                  <p className="text-xs text-muted-foreground">{t('webinar.basedOnSurvey')}</p>
                </div>
              </div>

              {/* Attendance curve */}
              <div className="bg-card border border-border rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> {t('webinar.attendanceCurve')}
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={w.attendanceCurve}>
                    <defs>
                      <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="t" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                    <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12 }} labelStyle={{ color: 'var(--foreground)' }} itemStyle={{ color: 'inherit' }} wrapperClassName="text-primary" />
                    <Area type="monotone" dataKey="count" stroke="var(--primary)" strokeWidth={2} fill="url(#aGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}

          {/* Recording */}
          {tab === 'recording' && (
            <motion.div key="rec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-4 sm:p-6">
              {w.recordings.length === 0
                ? <EmptyState icon={Video} title={t('webinar.noRecordingsAvailable')} description={t('webinar.recordingNotAvailable')} />
                : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {w.recordings.map(r => (
                      <div key={r.id} className="bg-card border border-border rounded-2xl overflow-hidden group">
                        <div className="relative aspect-video bg-muted">
                          <img src={r.thumb} alt={r.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-12 h-12 rounded-full bg-card/90 flex items-center justify-center shadow-lg">
                              <Play className="w-5 h-5 text-foreground ml-0.5" />
                            </div>
                          </div>
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded font-mono">{r.duration}</div>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-foreground mb-0.5">{r.name}</p>
                          <p className="text-xs text-muted-foreground mb-3">{r.size}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5">
                              <Download className="w-3.5 h-3.5" />{t('common.download')}
                            </Button>
                            <Button size="sm" className="flex-1 text-xs gap-1.5">
                              <Play className="w-3.5 h-3.5" />{t('webinar.play')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </motion.div>
          )}

          {/* Registrations */}
          {tab === 'registrations' && (
            <motion.div key="regs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-foreground">{registrations.length} {t('webinar.registrations')}</p>
                <Button variant="outline" size="sm" className="text-xs gap-1.5">
                  <Download className="w-3.5 h-3.5" />{t('webinar.export')}
                </Button>
              </div>
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">{t('webinar.name')}</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground hidden sm:table-cell">{t('common.email')}</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground hidden md:table-cell">{t('webinar.registered')}</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {registrations.map((reg, i) => (
                        <motion.tr key={reg.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-foreground">{reg.name}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground hidden sm:table-cell">{reg.email}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{reg.date}</td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="secondary"
                              className={`text-[10px] ${reg.status === 'Attended' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}
                            >
                              {reg.status}
                            </Badge>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* Analytics */}
          {tab === 'analytics' && (
            <motion.div key="an" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-4 sm:p-6 flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground mb-1">{t('webinar.fullAnalyticsAvailable')}</p>
                <p className="text-xs text-muted-foreground mb-4">{t('webinar.viewDetailedEngagement')}</p>
                <Button onClick={() => navigate(`/webinar/${id}/analytics`)} className="gap-2">
                  <Eye className="w-4 h-4" /> {t('webinar.viewFullAnalytics')}
                </Button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  )
}
